import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Globe, Heart } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import './About.css';

const milestones = [
  { year: 'Sep 2025', title: 'The Idea', desc: 'The founding team identified a massive gap in India\'s residential construction market - no single platform offered end-to-end, AI-powered home building. The idea for Sculpit was born.' },
  { year: 'Dec 2025', title: 'Research & Groundwork', desc: 'Deep market research began - talking to homeowners, contractors, architects, and lenders. Validated the pain points, studied competitors, and designed the product architecture.' },
  { year: 'Feb 2026', title: 'MVP Ready', desc: 'Built the first working version of the AI-powered 3D design studio with real-time cost estimation. Core platform features validated with early testers.' },
  { year: 'Mar 2026', title: 'Company Incorporated', desc: 'Sculpit Innovations Private Limited officially registered in Bengaluru, Karnataka. CIN: U43900KA2026PTC217041. The team formalized and operations began.' },
  { year: 'Now', title: 'Fundraising', desc: 'Currently raising our seed round to scale the platform, onboard construction partners, and expand the engineering team. Building partnerships with verified builders across South India.' },
  { year: 'Q4 2026 – Q1 2027', title: 'Public Launch', desc: 'Full platform launch planned with AI 3D design studio, real-time cost estimation, verified partner network, escrow payments, and home loan integration. Making home building stress-free for every Indian family.' },
];

const founders = [
  {
    name: 'Abhishek Madeshia',
    role: 'Founder & Chief Executive Officer (CEO)',
    bio: 'Founder of TechYzer, Dwibe, and Co-Founder of LYFAUX Technology Pvt. Ltd. Abhishek brings deep expertise in web, app, blockchain, and AR/VR development, with a strong entrepreneurial background in building scalable technology platforms.',
    img: '/screenshots/Abhishek.jpg',
    tag: 'Founder',
  },
  {
    name: 'Seshu Kumar',
    role: 'Founder & Chief Operating Officer (COO)',
    bio: 'Founder of LYFAUX Technology Pvt. Ltd. and Co-Founder of Dwibe. Seshu specializes in operations, execution, and organizational scaling across technology and education-focused ventures.',
    img: '/screenshots/Seshu.jpg',
    tag: 'Founder',
  },
];

const leadership = [
  {
    name: 'Yashas',
    role: 'VP, Marketing & Growth',
    bio: 'Growth and marketing leader with experience in real estate technology and performance marketing at Google. Previously led go-to-market, acquisition, and monetization strategies across multiple high-growth startups.',
    img: '/screenshots/Yashas.jpeg',
  },
  {
    name: 'Farha Anjum',
    role: 'Founding Member & VP, Design & Visualization',
    bio: 'Former Lead Frontend Developer at TechYzer turned self-taught architect. Farha plays a key role in building VR-ready architectural visualizations, realistic renders, and optimized pipelines from 2D blueprints to immersive 3D walkthroughs.',
    img: '/screenshots/Farha.jpeg',
  },
  {
    name: 'Rahul Raj M',
    role: 'Founding Member & VP Operations',
    bio: 'Founder and Director at Little Wings Innovation Labs India STEM. Rahul has extensive experience in STEM education, business development, curriculum design, strategic partnerships, and operational leadership.',
    img: '/screenshots/Rahul Raj.jpeg',
  },
];

const advisor = {
  name: 'Ajay Kumar J.K',
  role: 'Strategic Advisor',
  bio: 'Vice President – Mission Development (MEAP) at Helogen Corporation, leading In-Space Biomanufacturing & Space-Bio R&D. Former Founder & CEO of Vellon Space, acquired by Helogen Corp., USA.',
  img: '/screenshots/Ajay Kumar.jpg',
};

const values = [
  { icon: <div className="icon-wrap icon-wrap--coral icon-wrap--lg"><Heart /></div>, title: 'Homeowner First', desc: 'Every decision we make starts with one question: does this make building a home easier for the family?' },
  { icon: <div className="icon-wrap icon-wrap--amber icon-wrap--lg"><Zap /></div>, title: 'Radical Transparency', desc: 'No hidden costs, no vague timelines, no surprises. What you see is what you get - always.' },
  { icon: <div className="icon-wrap icon-wrap--blue icon-wrap--lg"><Globe /></div>, title: 'Technology for Everyone', desc: 'We build AI that your grandmother can use. Complex technology, simple experience.' },
];

export default function About() {
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero section-dark">
        <div className="about-hero__bg">
          <div className="about-hero__orb about-hero__orb--1" />
          <div className="about-hero__orb about-hero__orb--2" />
          <div className="about-hero__grid" />
        </div>
        <div className="container about-hero__content">
          <AnimatedSection>
            <span className="label label-teal">Our Story</span>
            <h1>We Tried Building a Home.<br /><span className="text-gradient">It Nearly Broke Us.</span></h1>
            <p className="about-hero__subtitle">
              So we built Sculpit - to make sure no Indian family ever has to go through what we went through. Building a home should be the most exciting chapter of your life, not the most stressful.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Mission */}
      <section className="section">
        <div className="container">
          <div className="about-mission">
            <AnimatedSection direction="left">
              <div className="about-mission__block">
                <h3 className="text-teal">Our Mission</h3>
                <p>Make home construction simple, transparent, and stress-free for every Indian family. Period.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection direction="right">
              <div className="about-mission__block">
                <h3 className="text-teal">Our Vision</h3>
                <p>Become India's most trusted construction platform by 2030 - serving every family that dreams of building their own home.</p>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection>
            <div className="about-values">
              {values.map((v, i) => (
                <div key={i} className="about-value card-premium">
                  {v.icon}
                  <h4>{v.title}</h4>
                  <p>{v.desc}</p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Team */}
      <section className="section section-gray">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">The People Behind Sculpit</span>
              <h2>Meet the Team</h2>
              <p>Entrepreneurs, engineers, and visionaries united by one goal - making home construction better for everyone.</p>
            </div>
          </AnimatedSection>

          {/* Founders - Featured */}
          <div className="about-founders-grid">
            {founders.map((member, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="about-founder-card card-premium">
                  <div className="about-founder-card__img-wrap">
                    <img src={member.img} alt={member.name} className="about-founder-card__img" />
                    <span className="about-founder-card__tag">{member.tag}</span>
                  </div>
                  <div className="about-founder-card__info">
                    <h3>{member.name}</h3>
                    <span className="about-team-card__role">{member.role}</span>
                    <p>{member.bio}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Leadership */}
          <AnimatedSection>
            <h3 className="about-team-subtitle">Leadership Team</h3>
          </AnimatedSection>
          <div className="about-team-grid">
            {leadership.map((member, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="about-team-card card-premium">
                  <div className="about-team-card__photo-wrap">
                    <img src={member.img} alt={member.name} className="about-team-card__photo" />
                  </div>
                  <h4>{member.name}</h4>
                  <span className="about-team-card__role">{member.role}</span>
                  <p>{member.bio}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Advisor */}
          <AnimatedSection>
            <h3 className="about-team-subtitle">Strategic Advisor</h3>
          </AnimatedSection>
          <AnimatedSection>
            <div className="about-advisor-card card-premium">
              <div className="about-team-card__photo-wrap">
                <img src={advisor.img} alt={advisor.name} className="about-team-card__photo" />
              </div>
              <div className="about-advisor-card__info">
                <h4>{advisor.name}</h4>
                <span className="about-team-card__role">{advisor.role}</span>
                <p>{advisor.bio}</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Timeline */}
      <section className="section">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">Our Journey</span>
              <h2>From Frustration to Platform</h2>
            </div>
          </AnimatedSection>

          <div className="about-timeline">
            {milestones.map((ms, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className={`about-timeline__item ${i % 2 !== 0 ? 'about-timeline__item--right' : ''}`}>
                  <div className="about-timeline__dot" />
                  <div className="about-timeline__card card-premium">
                    <span className="about-timeline__year">{ms.year}</span>
                    <h4>{ms.title}</h4>
                    <p>{ms.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
            <div className="about-timeline__line" />
          </div>
        </div>
      </section>

      {/* Market */}
      <section className="section section-dark">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <span className="label label-teal">The Opportunity</span>
              <h2>A $268 Billion Market, Waiting to Be Organized</h2>
              <p>
                India's residential construction market is one of the largest in the world - and one of the most fragmented. Less than 5% is organized. That's where Sculpit comes in.
              </p>
            </div>
          </AnimatedSection>

          <div className="about-market-stats">
            <AnimatedSection delay={0}>
              <div className="about-market-stat glass-card-dark">
                <span className="about-market-stat__val">$268B</span>
                <span className="about-market-stat__label">Residential Construction Market</span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.1}>
              <div className="about-market-stat glass-card-dark">
                <span className="about-market-stat__val">&lt;5%</span>
                <span className="about-market-stat__label">Currently Organized</span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="about-market-stat glass-card-dark">
                <span className="about-market-stat__val">10M+</span>
                <span className="about-market-stat__label">Homes Built Every Year</span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <div className="about-market-stat glass-card-dark">
                <span className="about-market-stat__val">35%</span>
                <span className="about-market-stat__label">Average Cost Overrun</span>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ textAlign: 'center' }}>
        <div className="container">
          <AnimatedSection>
            <h2>Want to Be Part of the Journey?</h2>
            <p className="cta-subtitle">
              Whether you're building a home or building a career - we'd love to have you.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/pricing" className="btn btn-primary btn-lg">
                Start Designing Free <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn btn-secondary btn-lg">
                Get in Touch
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
